import { Alert, Button, FormError } from "@simplicity/ui";
import { useState } from "react";
import {
  movesPlacement,
  settingsFormFrom,
  settingsPatch,
  sftpToggleNotice,
  type SettingsForm,
  type SftpToggleNotice,
} from "./onsiteModel";
import { PlacementFields } from "./PlacementFields";
import { ServerFields } from "./ServerFields";
import type { OnsiteWorkspace } from "./types";
import { useUpdateWorkspace } from "./useOnsite";

const ID = "onsite-settings";
const PLACEMENT_KEYS = ["region", "zone"] as const;

function MoveWarning() {
  return (
    <Alert variant="warning" className="sm:col-span-2">
      Changing the region or zone of an applied workspace plans a move: the VM, disks and buckets
      would be replaced, which the agent refuses for the disk, buckets and IP.
    </Alert>
  );
}

function sftpNoticeText(notice: SftpToggleNotice, gcpProjectId: string): string {
  if (notice === "on") {
    return "The next plan adopts the existing SFTP server, or creates one if there is none. An apply starts the VM.";
  }
  if (notice === "off-applied") {
    return "This workspace's server is managed by the agent, so the next plan will propose deleting it. The agent refuses to delete its bucket, secret, static IP and host-keys disk, so that plan can't be applied. Leave this on unless the server should be deleted.";
  }
  return `The agent will stop managing the SFTP server. Its existing resources stay in ${gcpProjectId} as they are, untouched and unmanaged. The next plan covers only the wrapper function.`;
}

function SftpNotice({
  workspace,
  enableSftpServer,
}: {
  workspace: OnsiteWorkspace;
  enableSftpServer: boolean;
}) {
  const notice = sftpToggleNotice(workspace, enableSftpServer);
  if (notice === null) return null;
  return (
    <Alert variant={notice === "on" ? "info" : "warning"} className="mb-4">
      {sftpNoticeText(notice, workspace.gcpProjectId)}
    </Alert>
  );
}

function WorkspaceSettingsForm({
  workspace,
  onSaved,
  onCancel,
}: {
  workspace: OnsiteWorkspace;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<SettingsForm>(() => settingsFormFrom(workspace));
  const update = useUpdateWorkspace(workspace.id);
  const patch = settingsPatch(workspace, form);
  const unchanged = Object.keys(patch).length === 0;
  const setField = <K extends keyof SettingsForm>(key: K, value: SettingsForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (unchanged) return;
    update.mutate(patch, { onSuccess: onSaved });
  }

  return (
    <form onSubmit={handleSubmit} className="form-card max-w-[640px]" aria-label="Edit settings">
      <h3 className="mb-1 text-sm font-semibold text-foreground">Edit settings</h3>
      <p className="mb-4 text-xs text-muted-foreground">
        The workspace name and GCP project can&apos;t be changed here.
      </p>
      <ServerFields
        idPrefix={ID}
        enableSftpServer={form.enableSftpServer}
        sshSourceRanges={form.sshSourceRanges}
        onSftpChange={(v) => setField("enableSftpServer", v)}
        onRangesChange={(v) => setField("sshSourceRanges", v)}
        notice={<SftpNotice workspace={workspace} enableSftpServer={form.enableSftpServer} />}
      />
      <PlacementFields
        idPrefix={ID}
        keys={PLACEMENT_KEYS}
        values={form}
        onChange={setField}
        notice={movesPlacement(workspace, patch) && <MoveWarning />}
      />
      {update.error && (
        <FormError assertive className="whitespace-pre-line">
          {update.error.message}
        </FormError>
      )}
      <div className="form-actions">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={update.isPending || unchanged}>
          {update.isPending ? "Saving..." : "Save settings"}
        </Button>
      </div>
    </form>
  );
}

/** Edits the workspace's Terraform settings; nothing reaches the client project until a plan. */
export function WorkspaceSettings({ workspace }: { workspace: OnsiteWorkspace }) {
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);

  if (editing) {
    return (
      <WorkspaceSettingsForm
        workspace={workspace}
        onCancel={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          setSaved(true);
        }}
      />
    );
  }
  return (
    <div className="space-y-3">
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          setSaved(false);
          setEditing(true);
        }}
      >
        Edit settings
      </Button>
      {saved && (
        <Alert variant="success">
          Settings saved. They take effect on the next plan: select Request plan below to see what
          changes.
        </Alert>
      )}
    </div>
  );
}
